import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import { disableAutorenew } from '../api';
import { useGlobalContext } from '../state/GlobalContext';

interface BaseMutationQuery {
  customerId?: string;
  subscriptionId?: string;
  authToken?: string;
}

const ConfirmCancelPrepaidMultiOrderModal: React.FC<{
  shouldExpireContract: boolean;
}> = ({ shouldExpireContract }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { onClose } = useModalManagerContext();
  const { authToken, customerId, subscriptionId } = useGlobalContext();
  const queryClient = useQueryClient();

  const handleOnClose = () => {
    onClose();
  };

  const callCancelSubscription = ({
    customerId,
    subscriptionId,
    authToken,
  }: BaseMutationQuery) => {
    setLoading(true);

    return disableAutorenew(customerId, subscriptionId, authToken);
  };

  const onCancelSubscriptionMutationSuccess = async () => {
    const previousSubscriptionValue = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);

    queryClient.setQueryData(['subscription', customerId, subscriptionId], {
      ...previousSubscriptionValue,
      BillingPolicyMaxCycles: 1,
      SubscriptionStatus: shouldExpireContract ? 'EXPIRED' : 'ACTIVE',
    });
    const successMsg = shouldExpireContract
      ? 'expireSuccess'
      : 'autoRenewDisabledSuccess';

    onClose(t(`cancelModal.prepaidMultiOrder.${successMsg}`));
  };

  const onCancelSubscriptionMutationError = () => {
    toast({
      isClosable: true,
      position: 'top',
      status: 'error',
      title: t('subscription.cancel_error'),
      variant: 'error',
    });
  };

  const onCancelSubscriptionMutationSettled = () => {
    setLoading(false);
  };

  const cancelSubscriptionMutation = useMutation(callCancelSubscription, {
    onSuccess: onCancelSubscriptionMutationSuccess,
    onError: onCancelSubscriptionMutationError,
    onSettled: onCancelSubscriptionMutationSettled,
  });

  const handleConfirmCancellationClick = () => {
    cancelSubscriptionMutation.mutate({
      customerId,
      subscriptionId,
      authToken,
    });
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.prepaidMultiOrder.modalTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <Text>
            {shouldExpireContract
              ? t('cancelModal.prepaidMultiOrder.expireDescription')
              : t('cancelModal.prepaidMultiOrder.description')}
          </Text>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={handleOnClose} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          isLoading={loading}
          onClick={handleConfirmCancellationClick}
          variant="blackBig"
        >
          {t('cancelModal.confirmPrepaid.button')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ConfirmCancelPrepaidMultiOrderModal;
